import Cookies from 'js-cookie';

const COOKIE_NAME = 'ym_fl';
const MAX_FREE_LISTENS = 2;
const COOKIE_EXPIRY_DAYS = 30;

export interface FreeListenData {
  count: number;
  meditationIds: string[];
  expiresAt: string;
}

/**
 * Get free listen data from cookie
 * Returns default data if cookie doesn't exist
 */
export function getFreeListenData(): FreeListenData {
  try {
    const cookieData = Cookies.get(COOKIE_NAME);

    if (!cookieData) {
      return {
        count: 0,
        meditationIds: [],
        expiresAt: new Date(Date.now() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString()
      };
    }

    const data: FreeListenData = JSON.parse(cookieData);

    // Check if cookie has expired
    if (new Date(data.expiresAt) < new Date()) {
      // Cookie expired, reset
      resetFreeListenData();
      return {
        count: 0,
        meditationIds: [],
        expiresAt: new Date(Date.now() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString()
      };
    }

    return data;
  } catch (error) {
    console.error('Error reading free listen cookie:', error);
    return {
      count: 0,
      meditationIds: [],
      expiresAt: new Date(Date.now() + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString()
    };
  }
}

/**
 * Check if user can listen to a meditation for free
 * Returns true if they haven't reached the limit OR if they've already listened to this specific meditation
 */
export function canListenForFree(meditationId: string): boolean {
  const data = getFreeListenData();

  // If they've already listened to this specific meditation, allow replay
  if (data.meditationIds.includes(meditationId)) {
    return true;
  }

  // Otherwise, check if they have free listens remaining
  return data.count < MAX_FREE_LISTENS;
}

/**
 * Record a free listen for a meditation
 * Only increments count if it's a NEW meditation (not replaying)
 */
export function recordFreeListen(meditationId: string): void {
  const data = getFreeListenData();

  // If this meditation was already listened to, don't count it again
  if (data.meditationIds.includes(meditationId)) {
    return;
  }

  // Add to meditation IDs and increment count
  data.meditationIds.push(meditationId);
  data.count = data.meditationIds.length;

  // Save to cookie (30 days expiry)
  Cookies.set(COOKIE_NAME, JSON.stringify(data), {
    expires: COOKIE_EXPIRY_DAYS,
    sameSite: 'lax' // CSRF protection
  });
}

/**
 * Get number of free listens remaining
 */
export function getRemainingFreeListens(): number {
  const data = getFreeListenData();
  return Math.max(0, MAX_FREE_LISTENS - data.count);
}

/**
 * Check if user has reached the free listen limit
 */
export function hasReachedFreeListenLimit(): boolean {
  const data = getFreeListenData();
  return data.count >= MAX_FREE_LISTENS;
}

/**
 * Check if user has used at least one free listen
 */
export function hasUsedAnyFreeListens(): boolean {
  const data = getFreeListenData();
  return data.count > 0;
}

/**
 * Reset free listen data (clear cookie)
 * Useful for testing or if user signs up
 */
export function resetFreeListenData(): void {
  Cookies.remove(COOKIE_NAME);
}

/**
 * Get all listened meditation IDs
 */
export function getListenedMeditationIds(): string[] {
  const data = getFreeListenData();
  return data.meditationIds;
}

// ===========================================
// COUPON COOKIE UTILITIES
// ===========================================

const COUPON_COOKIE_NAME = 'ym_coupon';
const COUPON_EXPIRY_DAYS = 30;

/**
 * Save coupon code to cookie
 * Used when user enters coupon or arrives via URL parameter
 */
export function setCouponCookie(code: string): void {
  try {
    Cookies.set(COUPON_COOKIE_NAME, code, {
      expires: COUPON_EXPIRY_DAYS,
      sameSite: 'lax'
    });
  } catch (error) {
    console.error('Error setting coupon cookie:', error);
  }
}

/**
 * Get coupon code from cookie
 * Returns null if no coupon is stored
 */
export function getCouponCookie(): string | null {
  try {
    const couponCode = Cookies.get(COUPON_COOKIE_NAME);
    return couponCode || null;
  } catch (error) {
    console.error('Error reading coupon cookie:', error);
    return null;
  }
}

/**
 * Clear coupon cookie
 * Called after successful subscription or when user removes coupon
 */
export function clearCouponCookie(): void {
  try {
    Cookies.remove(COUPON_COOKIE_NAME);
  } catch (error) {
    console.error('Error clearing coupon cookie:', error);
  }
}
